import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useUpdateSite } from '@/hooks/useSiteActions';
import type { Organization } from '@octave/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

type OrganizationSite = Organization['sites'][number];

const ADDRESS_FIELDS = ['street', 'zipCode', 'city', 'country'] as const;

/**
 * Sewan refuses a partial address, so the four core fields go together: all
 * filled, or all empty for a site whose address is not known yet.
 */
const siteFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Le nom est requis'),
    street: z.string().trim(),
    complement: z.string().trim(),
    zipCode: z.string().trim(),
    city: z.string().trim(),
    country: z.string().trim(),
  })
  .superRefine((values, ctx) => {
    const touchesAddress =
      values.complement.length > 0 ||
      ADDRESS_FIELDS.some((field) => values[field].length > 0);

    if (!touchesAddress) {
      return;
    }

    for (const field of ADDRESS_FIELDS) {
      if (values[field].length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Adresse incomplète',
        });
      }
    }
  });

type SiteFormValues = z.infer<typeof siteFormSchema>;

interface SiteEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site: OrganizationSite;
  organizationId: string;
}

export function SiteEditDialog({
  open,
  onOpenChange,
  site,
  organizationId,
}: SiteEditDialogProps) {
  const { mutate: updateSite, isPending } = useUpdateSite(organizationId);

  const form = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: {
      name: site.name,
      street: site.address?.street ?? '',
      complement: site.address?.complement ?? '',
      zipCode: site.address?.zipCode ?? '',
      city: site.address?.city ?? '',
      country: site.address?.country ?? '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: SiteFormValues) => {
    const hasAddress = ADDRESS_FIELDS.every((field) => data[field].length > 0);

    updateSite(
      {
        siteId: site.id,
        name: data.name,
        ...(hasAddress && {
          address: {
            street: data.street,
            zipCode: data.zipCode,
            city: data.city,
            country: data.country,
            ...(data.complement && { complement: data.complement }),
          },
        }),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifier le centre de facturation</DialogTitle>
          <DialogDescription>
            Ces informations sont enregistrées chez Sewan avant d'être
            répercutées dans Octave.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du site</FormLabel>
                  <FormControl>
                    <Input placeholder="Siège Lyon" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="street"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rue</FormLabel>
                  <FormControl>
                    <Input placeholder="12 rue de la Paix" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="complement"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Complément{' '}
                    <span className="text-muted-foreground text-xs">
                      (optionnel)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Bâtiment B, 3e étage" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code postal</FormLabel>
                    <FormControl>
                      <Input placeholder="69003" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ville</FormLabel>
                    <FormControl>
                      <Input placeholder="Lyon" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pays</FormLabel>
                    <FormControl>
                      <Input placeholder="France" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={
                  isPending ||
                  !form.formState.isValid ||
                  !form.formState.isDirty
                }
              >
                {isPending ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
